class TpCombo {
  constructor(container) {
    this.container = container;
    this.tierButtons = [...container.querySelectorAll(".tp-combo__tier")];
    this.rows = [...container.querySelectorAll(".tp-combo__flavor-row")];
    this.buyButton = container.querySelector(".tp-combo__buy-button");
    this.unitPriceEl = container.querySelector(".tp-combo__unit-price");
    this.comparePriceEl = container.querySelector(".tp-combo__compare-price");
    this.currentPriceEl = container.querySelector(".tp-combo__current-price");
    this.discountBadgeEl = container.querySelector(".tp-combo__discount-badge");
    this.installmentsEl = container.querySelector(".tp-combo__installments");
    this.messageEl = container.querySelector(".tp-combo__summary-message");
    this.progressFillEl = container.querySelector(".tp-combo__track-fill");
    this.pixDiscount = Number(container.dataset.pixDiscount || 0);
    this.installments = Number(container.dataset.installments || 1);

    if (
      !this.tierButtons.length ||
      !this.rows.length ||
      !this.buyButton ||
      !this.unitPriceEl ||
      !this.comparePriceEl ||
      !this.currentPriceEl ||
      !this.discountBadgeEl ||
      !this.installmentsEl ||
      !this.messageEl
    ) {
      return;
    }

    this.originalButtonText =
      this.buyButton.dataset.buttonLabel || this.buyButton.textContent.trim();

    this.sortTierButtons();
    this.selectedTier = this.readTier(this.tierButtons[0]);
    this.quantities = new Array(this.rows.length).fill(0);

    this.activateTier(this.tierButtons[0]);
    this.bindEvents();
    this.render();
  }

  readTier(button) {
    return {
      quantity: Number(button.dataset.quantity || 0),
      discount: Number(button.dataset.discount || 0),
    };
  }

  sortTierButtons() {
    this.tierButtons.sort(
      (current, next) =>
        Number(current.dataset.quantity || 0) -
        Number(next.dataset.quantity || 0),
    );

    const tiers = this.container.querySelector(".tp-combo__tiers");
    if (!tiers) return;

    this.tierButtons.forEach((button) => tiers.appendChild(button));
  }

  bindEvents() {
    this.tierButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.activateTier(button);
        this.render();
      });
    });

    this.rows.forEach((row, index) => {
      const minusButton = row.querySelector(".is-minus");
      const plusButton = row.querySelector(".is-plus");

      if (!minusButton || !plusButton) return;

      minusButton.addEventListener("click", () => {
        if (this.quantities[index] <= 0) return;
        this.quantities[index] -= 1;
        this.render();
      });

      plusButton.addEventListener("click", () => {
        this.quantities[index] += 1;
        this.render();
      });
    });

    this.buyButton.addEventListener("click", () => this.addToCart());
  }

  activateTier(button) {
    this.selectedTier = this.readTier(button);
    this.updateTierStates(this.getSelectedTotal(), button);
  }

  getReachedTierButton(selectedTotal) {
    return [...this.tierButtons]
      .reverse()
      .find(
        (button) =>
          selectedTotal >= Number(button.dataset.quantity || 0),
      );
  }

  getNextTierButton(selectedTotal) {
    return this.tierButtons.find(
      (button) => Number(button.dataset.quantity || 0) > selectedTotal,
    );
  }

  updateTierStates(selectedTotal, activeButton = null) {
    const reachedTier = this.getReachedTierButton(selectedTotal);
    const tierToActivate = activeButton || reachedTier || null;

    this.tierButtons.forEach((button) => {
      const isReached =
        selectedTotal >= Number(button.dataset.quantity || 0);
      const isActive = button === tierToActivate;

      button.classList.toggle("is-reached", isReached);
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    this.selectedTier = tierToActivate
      ? this.readTier(tierToActivate)
      : this.readTier(this.tierButtons[0]);
  }

  getSelectedTotal() {
    return this.quantities.reduce((sum, quantity) => sum + quantity, 0);
  }

  getBaseTotal() {
    return this.rows.reduce((sum, row, index) => {
      const price = Number(row.dataset.price || 0);
      return sum + price * this.quantities[index];
    }, 0);
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  }

  render() {
    const selectedTotal = this.getSelectedTotal();
    const reachedTierButton = this.getReachedTierButton(selectedTotal);
    const nextTierButton = this.getNextTierButton(selectedTotal);
    const discountTier = reachedTierButton
      ? this.readTier(reachedTierButton)
      : { quantity: 0, discount: 0 };

    this.updateTierStates(selectedTotal, reachedTierButton);
    this.updateProgressTrack(selectedTotal);

    const baseTotal = this.getBaseTotal();
    const discountedTotal = Math.round(
      baseTotal * ((100 - discountTier.discount) / 100),
    );
    const unitPrice =
      selectedTotal > 0 ? Math.round(discountedTotal / selectedTotal) : 0;
    const installmentsValue =
      this.installments > 0
        ? Math.round(discountedTotal / this.installments)
        : discountedTotal;

    this.rows.forEach((row, index) => {
      const value = row.querySelector(".tp-combo__stepper-value");
      if (value) value.textContent = this.quantities[index];
    });

    this.unitPriceEl.textContent =
      selectedTotal > 0 ? `${this.formatMoney(unitPrice)} cada` : "";
    this.unitPriceEl.hidden = selectedTotal === 0;
    this.comparePriceEl.textContent =
      discountTier.discount > 0 ? this.formatMoney(baseTotal) : "";
    this.comparePriceEl.hidden = discountTier.discount <= 0;
    this.currentPriceEl.textContent = this.formatMoney(discountedTotal);
    this.discountBadgeEl.textContent =
      discountTier.discount > 0 ? `${discountTier.discount}% OFF` : "";
    this.discountBadgeEl.hidden = discountTier.discount <= 0;
    this.discountBadgeEl.style.display =
      discountTier.discount > 0 ? "" : "none";
    this.installmentsEl.textContent =
      this.installments > 0
        ? `ou ${this.installments}x de ${this.formatMoney(installmentsValue)}`
        : "";

    if (selectedTotal === 0) {
      this.messageEl.textContent = "Selecione os sabores do combo.";
      this.buyButton.disabled = true;
    } else {
      this.messageEl.textContent = this.getProgressMessage(
        selectedTotal,
        nextTierButton,
        discountedTotal,
      );
      this.buyButton.disabled = false;
    }

    this.rows.forEach((row, index) => {
      const minusButton = row.querySelector(".is-minus");
      const plusButton = row.querySelector(".is-plus");
      if (!minusButton || !plusButton) return;

      minusButton.disabled = this.quantities[index] <= 0;
      plusButton.disabled = false;
    });
  }

  getProgressMessage(selectedTotal, nextTierButton, discountedTotal) {
    if (nextTierButton) {
      const nextTier = this.readTier(nextTierButton);
      const missing = nextTier.quantity - selectedTotal;
      const discountCopy =
        nextTier.discount > 0 ? ` e ganhe ${nextTier.discount}% OFF` : "";

      return `Adicione mais ${missing} ${missing === 1 ? "unidade" : "unidades"} para chegar em ${nextTier.quantity}${discountCopy}.`;
    }

    if (this.pixDiscount > 0) {
      return `${this.formatMoney(
        Math.round(discountedTotal * ((100 - this.pixDiscount) / 100)),
      )} no PIX`;
    }

    return "Combo pronto para adicionar ao carrinho.";
  }

  updateProgressTrack(selectedTotal = this.getSelectedTotal()) {
    const progress = this.getProgressByQuantity(selectedTotal);

    this.container
      .querySelector(".tp-combo__tiers")
      ?.style.setProperty("--tp-combo-progress", `${progress}%`);

    if (this.progressFillEl) {
      this.progressFillEl.style.width = `${progress}%`;
    }
  }

  getProgressByQuantity(selectedTotal) {
    if (this.tierButtons.length <= 1 || selectedTotal <= 0) return 0;

    const tiers = this.tierButtons.map((button) => this.readTier(button));
    const markerPosition = (index) =>
      ((index + 0.5) / this.tierButtons.length) * 100;
    const lastTierIndex = tiers.length - 1;

    if (selectedTotal >= tiers[lastTierIndex].quantity) return 100;

    const nextTierIndex = tiers.findIndex((tier) => tier.quantity >= selectedTotal);

    if (nextTierIndex < 0) return 100;
    if (nextTierIndex === 0) {
      const firstTierQuantity = tiers[0].quantity || 1;
      return (selectedTotal / firstTierQuantity) * markerPosition(0);
    }

    const previousTierIndex = nextTierIndex - 1;

    const previousTier = tiers[previousTierIndex];
    const nextTier = tiers[nextTierIndex];
    const tierRange = nextTier.quantity - previousTier.quantity;
    const segmentProgress =
      tierRange > 0 ? (selectedTotal - previousTier.quantity) / tierRange : 0;

    return (
      markerPosition(previousTierIndex) +
      segmentProgress *
        (markerPosition(nextTierIndex) - markerPosition(previousTierIndex))
    );
  }

  async addToCart() {
    if (this.buyButton.disabled) return;

    const items = this.rows
      .map((row, index) => ({
        id: Number(row.dataset.variantId),
        quantity: this.quantities[index],
      }))
      .filter((item) => item.quantity > 0);

    if (items.length === 0) return;

    try {
      this.buyButton.disabled = true;
      this.buyButton.textContent = "Adicionando...";

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error("Erro ao adicionar combo");

      this.buyButton.textContent = "✓ Adicionado!";
      await this.openCartDrawer();
    } catch (error) {
      console.error(error);
      this.buyButton.textContent = "Erro ao adicionar";
      this.buyButton.disabled = false;
      setTimeout(() => {
        this.buyButton.textContent = this.originalButtonText;
        this.render();
      }, 1800);
    }
  }

  async openCartDrawer() {
    const response = await fetch("/?sections=cart-drawer,cart-icon-bubble");
    const sections = await response.json();
    const drawer = document.querySelector("cart-drawer");

    if (drawer) {
      drawer.classList.remove("is-empty");
      drawer.renderContents({ sections });
      requestAnimationFrame(() => {
        drawer.classList.add("animate", "active");
        document.body.classList.add("overflow-hidden");
      });
    }

    setTimeout(() => {
      this.buyButton.textContent = this.originalButtonText;
      this.render();
    }, 1200);
  }
}

function initTpCombos(root = document) {
  root.querySelectorAll(".tp-combo").forEach((container) => {
    if (container.dataset.tpComboReady === "true") return;
    container.dataset.tpComboReady = "true";
    new TpCombo(container);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initTpCombos());
} else {
  initTpCombos();
}

document.addEventListener("shopify:section:load", (event) => {
  initTpCombos(event.target);
});
